import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import StatCard from '../components/patterns/stat-card';
import ActionCard from '../components/patterns/action-card';
import Icon from '../components/ui/icon';
import { useAuth } from '../contexts/AuthContext';
import { getMotivationalMessage } from '../lib/greeting';
import { getTierNameInVietnamese } from '../lib/outlet-utils';
import { orderApi } from '../services/orderApi';
import { missionApi } from '../services/missionApi';
import { promotionApi } from '../services/promotionApi';
import { userActivityService, UserActivity, UserStats } from '../services/userActivityService';
import { OrderStats } from '../types/order';
import { MissionSummary } from '../types/mission';

const Welcome: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Simple message state with fade effect
  const [displayedMessage, setDisplayedMessage] = useState('');
  const [isVisible, setIsVisible] = useState(true);
  
  // Order stats state
  const [orderStats, setOrderStats] = useState<OrderStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  
  // Mission stats state
  const [missionStats, setMissionStats] = useState<MissionSummary | null>(null);
  const [isLoadingMissionStats, setIsLoadingMissionStats] = useState(true);
  const [missionStatsError, setMissionStatsError] = useState<string | null>(null);
  
  // Promotion stats state
  const [promotionsCount, setPromotionsCount] = useState<number>(0);
  const [isLoadingPromotionsStats, setIsLoadingPromotionsStats] = useState(true);
  const [promotionsStatsError, setPromotionsStatsError] = useState<string | null>(null);
  
  // User activity state
  const [, setUserStats] = useState<UserStats | null>(null);
  const [recentActivities, setRecentActivities] = useState<UserActivity[]>([]);

  // Load order stats
  useEffect(() => {
    const loadOrderStats = async () => {
      if (!user?.outlet_id) {
        setIsLoadingStats(false);
        return;
      }

      try {
        setIsLoadingStats(true);
        setStatsError(null);
        const stats = await orderApi.getOrderStats(user.outlet_id);
        setOrderStats(stats);
      } catch (error) {
        setStatsError(error instanceof Error ? error.message : 'Không thể tải thống kê đơn hàng');
      } finally {
        setIsLoadingStats(false);
      }
    };

    loadOrderStats();
  }, [user?.outlet_id]);

  // Load mission stats
  useEffect(() => {
    const loadMissionStats = async () => {
      if (!user?.outlet_id) {
        setIsLoadingMissionStats(false);
        return;
      }

      try {
        setIsLoadingMissionStats(true);
        setMissionStatsError(null);
        const stats = await missionApi.getMissionStats(user.outlet_id);
        setMissionStats(stats);
      } catch (error) {
        setMissionStatsError(error instanceof Error ? error.message : 'Không thể tải thống kê nhiệm vụ');
      } finally {
        setIsLoadingMissionStats(false);
      }
    };

    loadMissionStats();
  }, [user?.outlet_id]);

  // Load promotion stats
  useEffect(() => {
    const loadPromotionStats = async () => {
      try {
        setIsLoadingPromotionsStats(true);
        setPromotionsStatsError(null);
        const count = await promotionApi.getPromotionsCount();
        setPromotionsCount(count);
      } catch (error) {
        setPromotionsStatsError(error instanceof Error ? error.message : 'Không thể tải thống kê khuyến mãi');
      } finally {
        setIsLoadingPromotionsStats(false);
      }
    };

    loadPromotionStats();
  }, []);

  // Load user activities and stats
  useEffect(() => {
    // Initialize sample data for MVP
    userActivityService.initializeSampleData();
    
    // Load user stats and activities
    const stats = userActivityService.getStats();
    const activities = userActivityService.getActivities(3);
    
    setUserStats(stats);
    setRecentActivities(activities);
  }, []);

  // Simple message effect with smooth transition
  useEffect(() => {
    const message = getMotivationalMessage();
    setDisplayedMessage(message);

    // Auto refresh message every 10 seconds
    const refreshInterval = setInterval(() => {
      // Fade out
      setIsVisible(false);
      
      // After fade out, change message and fade in
      setTimeout(() => {
        const newMessage = getMotivationalMessage();
        setDisplayedMessage(newMessage);
        setIsVisible(true);
      }, 300);
    }, 10000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  // Generate stats from API data or fallback to defaults
  const stats = [
    { 
      title: 'Tổng đơn', 
      value: orderStats?.total_orders || 0, 
      icon: 'shopping-cart', 
      trend: orderStats?.orders_trend ? { 
        value: orderStats.orders_trend.value, 
        isPositive: orderStats.orders_trend.is_positive 
      } : undefined,
      isLoading: isLoadingStats,
      onClick: () => navigate('/orders?filter=all')
    },
    { 
      title: 'Đang chờ', 
      value: orderStats?.pending_orders || 0, 
      icon: 'clock', 
      badge: orderStats?.pending_orders && orderStats.pending_orders > 0 ? 'Cần xử lý' : undefined,
      isLoading: isLoadingStats,
      onClick: () => navigate('/orders?filter=pending')
    },
    { 
      title: missionStats?.available_to_start && missionStats.available_to_start > 0 ? 'Nhiệm vụ mới' : 'Nhiệm vụ', 
      value: missionStats?.total_completed || 0,
      icon: 'target', 
      trend: missionStats?.total_completed && missionStats.total_completed > 0 ? { value: missionStats.total_completed, isPositive: true } : undefined,
      isLoading: isLoadingMissionStats,
      onClick: () => navigate('/missions')
    },
    { 
      title: promotionsCount > 0 ? 'Khuyến mãi hot' : 'Khuyến mãi', 
      value: promotionsCount,
      icon: 'gift', 
      isLoading: isLoadingPromotionsStats,
      onClick: () => navigate('/promotions')
    },
  ];

  // Generate dynamic messages based on action and user context
  const getActionMessage = (action: string) => {
    const messages = {
      general: `Xin chào! Hôm nay có gì mới cho cửa hàng không?`,
      orders: `Em gợi ý đơn nhập hàng tuần này cho cửa hàng nhé!`,
      missions: `Có nhiệm vụ tích điểm nào cho cửa hàng không?`,
      promotions: `Hôm nay có ưu đãi gì cho cửa hàng không? `
    };
    return messages[action as keyof typeof messages] || `Xin chào, hôm nay có gì hấp dẫn không?`;
  };

  const quickActions = [
    {
      title: 'Hỏi-Đáp',
      description: 'Tra cứu sản phẩm, khuyến mãi, chính sách...',
      icon: 'message-circle',
      color: 'blue' as const,
      onClick: () => navigate(`/assistant?action=general&message=${encodeURIComponent(getActionMessage('general'))}`)
    },
    {
      title: 'Nhập hàng thông minh',
      description: 'Gợi ý nhập hàng cho cửa hàng',
      icon: 'shopping-cart',
      color: 'green' as const,
      onClick: () => navigate(`/assistant?action=orders&message=${encodeURIComponent(getActionMessage('orders'))}`)
    },
    {
      title: 'Làm nhiệm vụ tích điểm',
      description: 'Tham gia nhiệm vụ để nhận điểm thưởng',
      icon: 'target',
      color: 'purple' as const,
      onClick: () => navigate(`/assistant?action=missions&message=${encodeURIComponent(getActionMessage('missions'))}`)
    },
    {
      title: 'Xem khuyến mãi',
      description: 'Xem khuyến mãi đang áp dụng cho cửa hàng',
      icon: 'gift',
      color: 'orange' as const,
      onClick: () => navigate(`/assistant?action=promotions&message=${encodeURIComponent(getActionMessage('promotions'))}`)
    }
  ];


  return (
    <div className="max-w-4xl mx-auto px-4 py-2 space-y-6">
      {/* Welcome Header */}
      <Card className="bg-primary text-primary-foreground">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <span className="text-2xl font-bold line-clamp-1">
                {user?.name || 'Bạn'}
              </span>
              <span className="text-primary-foreground/80 text-sm uppercase line-clamp-1">
                {user?.contact_person || 'Bạn'} • {user?.phone || ''}
              </span>
              <span className="bg-yellow-500 dark:bg-yellow-500/80 w-fit px-2 py-0.5 rounded-md text-white text-sm">
                Hạng {getTierNameInVietnamese(user?.scoring?.score_tier || 'Gold')}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-center">
                <div className="text-3xl font-bold">
                  {user?.scoring?.overall_score || 0}
                </div>
                <div className="text-md opacity-80">Điểm</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="flex items-center gap-2 justify-center line-clamp-1"> 
        <span 
          className={`text-lg text-center text-muted-foreground transition-opacity duration-300 ${
            isVisible ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {displayedMessage}
        </span>
      </div>

      {/* Stats Grid */}
      <div className="space-y-4"> 
        {(statsError || missionStatsError || promotionsStatsError) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="alert-circle" size={20} className="text-red-500 mr-2" />
              <span className="text-red-700">{statsError || missionStatsError || promotionsStatsError}</span>
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              trend={stat.trend}
              badge={stat.badge}
              isLoading={stat.isLoading}
              onClick={stat.onClick}
            />
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="space-y-4 pt-2">
        <h2 className="text-xl font-bold">Thao tác nhanh</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {quickActions.map((action, index) => (
            <ActionCard
              key={index}
              title={action.title}
              description={action.description}
              icon={action.icon}
              color={action.color}
              onClick={action.onClick}
            />
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="space-y-4 pt-2">
        <h2 className="text-xl font-bold">Hoạt động gần đây</h2>
        <Card>
          <CardContent className="p-0">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => (
                <div key={activity.id} className="flex items-center gap-4 p-6 border-b last:border-b-0">
                  <Icon name={activity.icon as any} size={20} />
                  <div className="flex-1">
                    <p className="text-md line-clamp-1">{activity.title}</p>
                    <p className="text-sm text-muted-foreground line-clamp-1">
                      {activity.timestamp.toLocaleString('vi-VN', {
                        hour: '2-digit',
                        minute: '2-digit',
                        day: '2-digit',
                        month: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-6 text-center text-muted-foreground">
                <Icon name="clock" size={24} className="mx-auto mb-2" />
                <p>Chưa có hoạt động nào</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Welcome;
